/**
 * Drug Results Table Component
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Checkbox,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import {
  MRT_FullScreenToggleButton as MrtFullScreenToggleButton,
  MRT_ShowHideColumnsButton as MrtShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as MrtToggleDensePaddingButton,
  MRT_ToggleFiltersButton as MrtToggleFiltersButton,
  MRT_ToggleGlobalFilterButton as MrtToggleGlobalFilterButton,
} from 'material-react-table';
import GlobalMaterialTable from '../../../../components/common/GlobalMaterialTable';
import { ImSpinner2 } from 'react-icons/im';
import { FiDownload } from 'react-icons/fi';

const DrugResultsTable = ({
  results,
  brandOptions,
  brandTypeOptions,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onExport,
  exportLoading,
  onSaveEdits,
  savingEdits,
  loading,
}) => {
  const theme = useTheme();
  const [selectedRowIds, setSelectedRowIds] = React.useState({});
  const [draftsByRow, setDraftsByRow] = React.useState({});
  const [showChangedOnly, setShowChangedOnly] = React.useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);

  React.useEffect(() => {
    const validIds = new Set((results || []).map((row) => String(row.id)));
    setSelectedRowIds((prev) => Object.keys(prev).reduce((acc, id) => {
      if (validIds.has(id)) acc[id] = true;
      return acc;
    }, {}));
    setDraftsByRow((prev) => Object.keys(prev).reduce((acc, id) => {
      if (validIds.has(id)) acc[id] = prev[id];
      return acc;
    }, {}));
  }, [results]);

  const selectedCount = useMemo(
    () => Object.values(selectedRowIds).filter(Boolean).length,
    [selectedRowIds]
  );

  const allSelected = useMemo(
    () => results.length > 0 && selectedCount === results.length,
    [results, selectedCount]
  );

  const toClientDrugString = (value) =>
    (value === true || String(value).toLowerCase() === 'true' ? 'true' : 'false');

  const getDraftField = (row, fieldName) => {
    const draft = draftsByRow[String(row.id)] || {};
    if (fieldName === 'clientDrugFlag') {
      if (draft.clientDrugFlag !== undefined) return toClientDrugString(draft.clientDrugFlag);
      return toClientDrugString(row.clientDrugFlag ?? row.clientDrugs);
    }
    return draft[fieldName] ?? row[fieldName] ?? '';
  };

  const upsertDraft = (row, patch) => {
    setDraftsByRow((prev) => ({
      ...prev,
      [String(row.id)]: {
        brandName: prev[String(row.id)]?.brandName ?? row.brandName ?? '',
        clientDrugFlag:
          prev[String(row.id)]?.clientDrugFlag
          ?? (row.clientDrugFlag === true || String(row.clientDrugs).toLowerCase() === 'true'),
        clientBrandType: prev[String(row.id)]?.clientBrandType ?? row.clientBrandType ?? '',
        ...patch,
      },
    }));
  };

  const toggleSelectAll = (checked) => {
    if (!checked) {
      setSelectedRowIds({});
      return;
    }
    const next = {};
    results.forEach((row) => {
      next[String(row.id)] = true;
    });
    setSelectedRowIds(next);
  };

  const toggleRowSelection = (rowId, checked) => {
    const key = String(rowId);
    setSelectedRowIds((prev) => ({ ...prev, [key]: checked }));
  };

  const changedRowsPayload = useMemo(() => {
    const selectedIds = new Set(
      Object.keys(selectedRowIds).filter((id) => selectedRowIds[id])
    );

    return (results || [])
      .filter((row) => selectedIds.has(String(row.id)))
      .map((row) => {
        const draft = draftsByRow[String(row.id)];
        if (!draft) return null;

        const nextBrandName = (draft.brandName ?? '').trim();
        const currentBrandName = (row.brandName ?? '').trim();
        const nextClientDrugFlag =
          draft.clientDrugFlag === true || String(draft.clientDrugFlag).toLowerCase() === 'true';
        const currentClientDrugFlag =
          row.clientDrugFlag === true || String(row.clientDrugs).toLowerCase() === 'true';
        const nextClientBrandType = (draft.clientBrandType ?? row.clientBrandType ?? '').trim();
        const currentClientBrandType = (row.clientBrandType ?? '').trim();
        const regularChanged = nextBrandName !== currentBrandName || nextClientDrugFlag !== currentClientDrugFlag;
        const brandTypeChanged = nextClientBrandType !== currentClientBrandType;

        if (!regularChanged && !brandTypeChanged) {
          return null;
        }

        return {
          id: row.id,
          drugId: row.drugId,
          drugName: row.drugName,
          brandId: row.brandId,
          ndc: row.ndc,
          brandName: nextBrandName || currentBrandName,
          clientDrugFlag: nextClientDrugFlag,
          clientBrandType: nextClientBrandType,
          currentBrandName,
          currentClientDrugFlag,
          currentClientBrandType,
          regularChanged,
          brandTypeChanged,
        };
      })
      .filter(Boolean);
  }, [draftsByRow, results, selectedRowIds]);

  const confirmationRows = useMemo(
    () => changedRowsPayload.map((item) => {
      const fieldChanges = [];

      if (item.regularChanged) {
        if (item.currentBrandName !== item.brandName) {
          fieldChanges.push({
            label: 'Brand Name',
            from: item.currentBrandName || '-',
            to: item.brandName || '-',
          });
        }

        if (item.currentClientDrugFlag !== item.clientDrugFlag) {
          fieldChanges.push({
            label: 'Client Drug',
            from: toClientDrugString(item.currentClientDrugFlag),
            to: toClientDrugString(item.clientDrugFlag),
          });
        }
      }

      if (item.brandTypeChanged) {
        fieldChanges.push({
          label: 'Brand Type',
          from: item.currentClientBrandType || '-',
          to: item.clientBrandType || '-',
        });
      }

      return {
        ...item,
        fieldChanges,
      };
    }),
    [changedRowsPayload]
  );

  const hasRowChanges = React.useCallback((row) => {
    const draft = draftsByRow[String(row.id)];
    if (!draft) return false;

    const nextBrandName = (draft.brandName ?? '').trim();
    const currentBrandName = (row.brandName ?? '').trim();
    const nextClientDrugFlag =
      draft.clientDrugFlag === true || String(draft.clientDrugFlag).toLowerCase() === 'true';
    const currentClientDrugFlag =
      row.clientDrugFlag === true || String(row.clientDrugs).toLowerCase() === 'true';
    const nextClientBrandType = (draft.clientBrandType ?? row.clientBrandType ?? '').trim();
    const currentClientBrandType = (row.clientBrandType ?? '').trim();

    return nextBrandName !== currentBrandName
      || nextClientDrugFlag !== currentClientDrugFlag
      || nextClientBrandType !== currentClientBrandType;
  }, [draftsByRow]);

  const displayedRows = useMemo(
    () => (showChangedOnly ? (results || []).filter((row) => hasRowChanges(row)) : results),
    [hasRowChanges, results, showChangedOnly]
  );

  const handleSave = () => {
    if (changedRowsPayload.length === 0) return;
    setConfirmDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmDialogOpen(false);
    await onSaveEdits(changedRowsPayload);
  };

  const tableColumns = useMemo(() => ([
    {
      header: (
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={selectedCount > 0 && !allSelected}
          onChange={(event) => toggleSelectAll(event.target.checked)}
        />
      ),
      id: 'select',
      enableSorting: false,
      size: 40,
      Cell: ({ row }) => (
        <Checkbox
          size="small"
          checked={Boolean(selectedRowIds[String(row.original.id)])}
          onChange={(event) => toggleRowSelection(row.original.id, event.target.checked)}
          onClick={(event) => event.stopPropagation()}
        />
      ),
    },
    { header: 'NDC', id: 'ndc', accessorFn: (row) => row.ndc || '-' },
    { header: 'Drug Name', id: 'drugName', accessorFn: (row) => row.drugName || '-' },
    {
      header: 'Brand Name',
      id: 'brandName',
      Cell: ({ row }) => {
        const selected = Boolean(selectedRowIds[String(row.original.id)]);
        return (
          <TextField
            select
            size="small"
            fullWidth
            value={getDraftField(row.original, 'brandName')}
            onChange={(event) => upsertDraft(row.original, { brandName: event.target.value })}
            disabled={!selected}
            onClick={(event) => event.stopPropagation()}
          >
            {(brandOptions || []).map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        );
      },
    },
    { header: 'Form TLC3', id: 'formTlc3', accessorFn: (row) => row.formTlc3 || '-' },
    {
      header: 'Client Drugs',
      id: 'clientDrugs',
      Cell: ({ row }) => {
        const selected = Boolean(selectedRowIds[String(row.original.id)]);
        return (
          <TextField
            select
            size="small"
            value={getDraftField(row.original, 'clientDrugFlag')}
            onChange={(event) =>
              upsertDraft(row.original, { clientDrugFlag: event.target.value === 'true' })}
            disabled={!selected}
            onClick={(event) => event.stopPropagation()}
            sx={{ minWidth: 110 }}
          >
            <MenuItem value="true">true</MenuItem>
            <MenuItem value="false">false</MenuItem>
          </TextField>
        );
      },
    },
    { header: 'Manufacturer Name', id: 'manufacturerName', accessorFn: (row) => row.manufacturerName || '-' },
    { header: 'Product Sum', id: 'productSum', accessorFn: (row) => row.productSum || '-' },
    {
      header: 'Brand Type',
      id: 'brandType',
      Cell: ({ row }) => {
        const selected = Boolean(selectedRowIds[String(row.original.id)]);
        return (
          <TextField
            select
            size="small"
            fullWidth
            value={getDraftField(row.original, 'clientBrandType')}
            onChange={(event) => upsertDraft(row.original, { clientBrandType: event.target.value })}
            disabled={!selected}
            onClick={(event) => event.stopPropagation()}
          >
            <MenuItem value="" disabled>Select Brand Type</MenuItem>
            {(brandTypeOptions || []).map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        );
      },
    },
    { header: 'PGN Key', id: 'pgnKey', accessorFn: (row) => row.pgnKey || '-' },
    { header: 'PGN Description', id: 'pgnDescription', accessorFn: (row) => row.pgnDescription || '-' },
  ]), [
    allSelected,
    brandOptions,
    brandTypeOptions,
    getDraftField,
    selectedCount,
    selectedRowIds,
    toggleSelectAll,
  ]);

  if (!results || results.length === 0 && !loading) {
    return (
      <Paper sx={{ p: 8, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">No results found</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Try adjusting your search criteria</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <GlobalMaterialTable
        columns={tableColumns}
        data={displayedRows}
        isLoading={loading}
        tableTitle="Drug Search Results"
        tableCount={totalCount}
        renderToolbarInternalActions={({ table }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MrtToggleGlobalFilterButton table={table} />
            <MrtToggleFiltersButton table={table} />
            <MrtShowHideColumnsButton table={table} />
            <MrtToggleDensePaddingButton table={table} />
            <MrtFullScreenToggleButton table={table} />
            <Tooltip title="Export">
              <IconButton
                onClick={onExport}
                disabled={exportLoading || results.length === 0}
                sx={{
                  color: '#475569',
                  '&.Mui-disabled': {
                    color: '#94a3b8',
                    cursor: 'not-allowed',
                    pointerEvents: 'auto',
                  }
                }}
              >
                {exportLoading ? (
                  <ImSpinner2 size={15} className="spinner-icon" />
                ) : (
                  <FiDownload size={18} />
                )}
              </IconButton>
            </Tooltip>
            {/* <Button className="action-btn close-btn"
              variant={showChangedOnly ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setShowChangedOnly((prev) => !prev)}
            >
              Changed only
            </Button> */}
            <Button className="action-btn process-btn"
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={savingEdits || changedRowsPayload.length === 0}
              sx={{
                borderRadius: '8px',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 2px 4px rgba(30, 136, 229, 0.2)',
                  '&.Mui-disabled': {
                  backgroundColor:"#ebe7e7",
                  cursor: 'not-allowed',
                  pointerEvents: 'auto', // required for cursor to work on disabled buttons
                },
              }}
            >
              {savingEdits ? 'Saving...' : `Update (${changedRowsPayload.length})`}
            </Button>
          </Box>
        )}
        muiSearchTextFieldProps={{ placeholder: 'Search drug results...' }}
        enableStickyHeader
        manualPagination={!showChangedOnly}
        enablePagination
        rowCount={showChangedOnly ? displayedRows.length : totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        muiTableContainerProps={{
          sx: { maxHeight: 520, overflowX: 'auto', overflowY: 'auto' },
        }}
        muiTableBodyRowProps={{
          sx: {
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          },
        }}
        muiTableHeadCellProps={{ sx: { whiteSpace: 'nowrap' } }}
        muiTableBodyCellProps={{ sx: { whiteSpace: 'nowrap' } }}
        OuterBorder={"none"}
      />
      <Dialog
        open={confirmDialogOpen}
        onClose={() => !savingEdits && setConfirmDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Confirm Drug Updates</DialogTitle>
        <DialogContent dividers>
          {confirmationRows.map((row, index) => (
            <Box key={`${row.id}-${row.ndc}-${index}`} sx={{ py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {row.drugName || '-'} ({row.ndc || '-'})
              </Typography>
              {row.fieldChanges.map((change) => (
                <Box
                  key={`${row.id}-${change.label}`}
                  sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {change.label}:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {change.from}
                  </Typography>
                  <Typography variant="body2">{'->'}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {change.to}
                  </Typography>
                </Box>
              ))}
              {index < confirmationRows.length - 1 && <Divider sx={{ mt: 1.5 }} />}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button className="action-btn close-btn" onClick={() => setConfirmDialogOpen(false)} disabled={savingEdits}>
            Close
          </Button>
          <Button className="action-btn process-btn" variant="contained" onClick={handleConfirmSave} disabled={savingEdits}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

DrugResultsTable.propTypes = {
  results: PropTypes.array.isRequired,
  brandOptions: PropTypes.arrayOf(PropTypes.string),
  brandTypeOptions: PropTypes.arrayOf(PropTypes.string),
  totalCount: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  exportLoading: PropTypes.bool,
  onSaveEdits: PropTypes.func.isRequired,
  savingEdits: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
};

DrugResultsTable.defaultProps = {
  brandOptions: [],
  brandTypeOptions: [],
  savingEdits: false,
};

export default DrugResultsTable;
